"use client";

import React from "react";
import { formatRelativeTime } from "utils/formatRelativeTime";
import { RelativeTime } from "modules/common/components/ui/RelativeTime";
import { useHydrated } from "modules/common/hooks/useHydrated";
import { FinishEnum } from "modules/cards/model/card";
import { EmbeddedCardPrice } from "modules/cards/model/card-price";
import {
  cn,
  getFinishPrice,
  getSpecificPrice,
  isPriceApproximate,
} from "utils/utils";
import { finishLabel, finishSpec } from "modules/cards/model/finishBehavior";

// Structural type so any card-shaped record (GameCard, TradeItem,
// CollectionCard, DeckCard, BaseCardItem, …) is acceptable. We only need
// the price + finishes off it.
type CardLike = {
  price?: EmbeddedCardPrice;
  finishes: FinishEnum[];
};

interface CardPrimaryPriceProps {
  card: CardLike;
  className?: string;
  /** Whether to render the relative "Updated 3d ago" string inline next
   *  to the price. Off by default (compact contexts use the tooltip).
   *  Sites with room (modal CTA, deck hero) opt in. */
  showRelativeTimestamp?: boolean;
  /** Rendered when the card has no price at all (`value.default <= 0`). */
  emptyFallback?: React.ReactNode;
  /** Optional override for the tooltip placement; defaults to top. */
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  /**
   * When set, render the price for *this specific finish* off the (multi-
   * finish) master card rather than the cross-finish `value.default`.
   * Used on the card details modal/page when the user opened a foil
   * trade-item — they expect to see the foil price, not the master's
   * nonfoil-derived default. Falls back to the default-with-approximate
   * marker when the requested finish has no chart data.
   */
  finish?: FinishEnum;
}

function finishTimestamp(
  price: EmbeddedCardPrice | undefined,
  finish: FinishEnum
): string | undefined {
  if (!price) return undefined;
  const key = finishSpec(finish).timestampKey;
  if (!key) return price.timestamp;
  const ts = price[key];
  return typeof ts === "string" ? ts : price.timestamp;
}

function approximateSourceCopy(source: FinishEnum | null): string {
  return source
    ? `From ${finishLabel(source).toLowerCase()} price`
    : "Approximate price";
}

/**
 * Renders a card's primary/any-price (`value.default`) with two trust
 * signals layered on top:
 *
 *   1. `≈` prefix when the price is approximate — i.e. `value.default`
 *      was mirrored from foil/etched by the backend because no real
 *      nonfoil chart source exists. Derived via `isPriceApproximate`,
 *      no metadata flag required.
 *   2. Tooltip with the freshness ("Updated 3d ago", absolute date)
 *      plus the approximation explanation when applicable.
 *
 * Compact by default; pass `showRelativeTimestamp` to also render the
 * relative time visibly inline (used by hero / CTA surfaces with room).
 */
export function CardPrimaryPrice({
  card,
  className,
  showRelativeTimestamp = false,
  emptyFallback = null,
  tooltipPlacement = "top",
  finish,
}: CardPrimaryPriceProps) {
  const hydrated = useHydrated();

  // When a specific finish is requested and the master carries a real
  // per-finish value, render that — explicitly, no ≈ marker — and read
  // the matching per-finish timestamp for the tooltip. If no chart data
  // exists for the requested finish, fall through to the default flow
  // so the user still sees an approximation rather than nothing.
  const finishLabel = finish ? getFinishPrice(card.price, finish) : "";
  const usingFinishOverride = finishLabel !== "";
  const priceLabel = usingFinishOverride
    ? finishLabel
    : getSpecificPrice(card.price, false);
  if (!priceLabel) return <>{emptyFallback}</>;

  const approximation = usingFinishOverride
    ? { approximate: false, source: null as FinishEnum | null }
    : isPriceApproximate(card);
  const { approximate, source } = approximation;
  const tsIso =
    usingFinishOverride && finish
      ? finishTimestamp(card.price, finish)
      : card.price?.timestamp;
  const timestamp = tsIso ? new Date(tsIso) : null;
  // The tooltip text goes into an attribute, so it can't use
  // suppressHydrationWarning — hold it back until after mount instead.
  const relative = hydrated && timestamp ? formatRelativeTime(timestamp) : null;

  const tooltipParts: string[] = [];
  if (approximate) tooltipParts.push(approximateSourceCopy(source));
  if (relative) tooltipParts.push(`Updated ${relative}`);
  const tooltipText = tooltipParts.join(" · ");
  const hasTooltip = tooltipText.length > 0;

  const placementClass =
    tooltipPlacement === "top"
      ? "lg:tooltip-top"
      : tooltipPlacement === "bottom"
        ? "lg:tooltip-bottom"
        : tooltipPlacement === "left"
          ? "lg:tooltip-left"
          : "lg:tooltip-right";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1",
        hasTooltip && `lg:tooltip ${placementClass}`,
        // Approximate prices read as "soft estimate" via muted text —
        // the user clocks the visual softness even without hovering
        // for the tooltip. ≈ marker shares the same muted treatment.
        approximate && "text-base-content/55",
        className
      )}
      data-tip={hasTooltip ? tooltipText : undefined}
    >
      {approximate && <span aria-label="approximate price">≈</span>}
      <span>{priceLabel}</span>
      {showRelativeTimestamp && timestamp && (
        <RelativeTime
          date={timestamp}
          className="text-base-content/50 ml-1 text-xs"
        />
      )}
    </span>
  );
}
